import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {isShallowEqual} from '../../../helpers/reselectHelpers';
import {AppAction} from '../../appActions';
import {DataCascade} from '../../data/dataCascade';
import {PanelCancelTokenId} from '../panelCancelTokenId';
import {
  CellsPanelActionsEnum,
  CellsPanelConversationsActionsEnum,
  CellsPanelMessagesActionsEnum,
  CellsPanelPageActionsEnum,
} from './cellsPanelActions';
import {
  CellsPanelPagesState,
  CellsPanelPageState,
  CellsPanelPageTypesEnum,
  CellsPanelState,
} from './cellsPanelState';
import {cellsPanelConversationsReducer} from './conversations/cellsPanelConversationsReducer';
import {cellsPanelMessagesReducer} from './messages/cellsPanelMessagesReducer';
import {cellsPanelSearchReducer} from './search/cellsPanelSearchReducer';

/*
 * Cells.
 */

export function cellsPanelReducer(
  state: CellsPanelState,
  action: AppAction,
  dataCascade: DataCascade,
): CellsPanelState {
  const newState: CellsPanelState = {
    ...state,
    pages: cellsPanelPagesReducer(state.pages, action, dataCascade),
    search: cellsPanelSearchReducer(state.search, action),
    cancelTokenId: cellsPanelCancelTokenIdReducer(state.cancelTokenId, action),
  };

  if (isShallowEqual(newState, state)) {
    return state;
  }

  return newState;
}

/*
 * Cells pages.
 */

function cellsPanelPagesReducer(
  state: CellsPanelPagesState,
  action: AppAction,
  dataCascade: DataCascade,
): CellsPanelPagesState {
  switch (action.type) {
    case CellsPanelActionsEnum.NAVIGATED:
      // If the current page never loaded, replace it with the new page immediately.
      if (state.current.status !== AsyncStatusesEnum.LOADED) {
        return {
          current: action.payload.page,
          next: undefined,
        };
      }

      // Otherwise, put the new page on stand by.
      return {
        ...state,
        next: action.payload.page,
      };

    // If this is a timeout action, replace the main page if needed.
    case CellsPanelPageActionsEnum.FETCH_CELLS_GRACE_TIMEOUT: {
      const {next} = state;
      if (!next || next.uid !== action.payload.pageUid) {
        return state;
      }

      return {
        current: next,
        next: undefined,
      };
    }

    // If this is a refetch or completion action, replace the main page.
    case CellsPanelPageActionsEnum.REFETCH_STARTED:
    case CellsPanelConversationsActionsEnum.FETCH_CONVERSATIONS_COMPLETED:
    case CellsPanelConversationsActionsEnum.FETCH_CONVERSATIONS_FAILED:
    case CellsPanelMessagesActionsEnum.FETCH_MESSAGES_COMPLETED:
    case CellsPanelMessagesActionsEnum.FETCH_MESSAGES_FAILED: {
      const page = findCellsPanelPage(state, action.payload.pageUid);
      if (!page) {
        return state;
      }

      return {
        current: cellsPanelPageReducer(page, action, dataCascade),
        next: undefined,
      };
    }

    // Otherwise, reduce every page.
    default: {
      const {current, next} = state;
      const newState = {
        current: cellsPanelPageReducer(current, action, dataCascade),
        next: next && cellsPanelPageReducer(next, action, dataCascade),
      };

      if (isShallowEqual(newState, state)) {
        return state;
      }

      return newState;
    }
  }
}

/*
 * Cells page.
 */

function cellsPanelPageReducer(state: CellsPanelPageState, action: AppAction, dataCascade: DataCascade) {
  switch (state.type) {
    case CellsPanelPageTypesEnum.CONVERSATIONS:
      return cellsPanelConversationsReducer(state, action, dataCascade);

    default:
      return cellsPanelMessagesReducer(state, action, dataCascade);
  }
}

/*
 * Cancel token.
 */

function cellsPanelCancelTokenIdReducer(state: PanelCancelTokenId, action: AppAction): PanelCancelTokenId {
  switch (action.type) {
    case CellsPanelActionsEnum.NAVIGATED:
    case CellsPanelPageActionsEnum.REFETCH_STARTED:
      return action.payload.cancelTokenId;

    default:
      return state;
  }
}

/*
 * Helpers.
 */

function findCellsPanelPage(state: CellsPanelPagesState, pageUid: string) {
  return [state.next, state.current].find((page) => page?.uid === pageUid);
}
